import type { FC, FormEvent, ReactNode } from 'react'
import type { FieldErrors, TicketListField, TicketListResponse, TicketListStatus } from '@/lib/entries'

import { useState } from 'react'
import { CircleCheck, LoaderCircle } from 'lucide-react'
import { buttonBase, buttonVariants } from '@/components/landing/button-link'
import { cn } from '@/lib/utils'
import { MAX_PARTY_SIZE, readTicketList, validateTicketList } from '@/lib/entries'

import {
    Field,
    HoneypotField,
    SelectShell,
    inputClasses,
    selectClasses
} from '@/components/landing/form-field'

import { EVENT, PASSES } from '@/constants/event'

interface TicketListFormProps {
    pass: string
    onPassChange: (pass: string) => void
}

type FormState =
    | { kind: 'idle' }
    | { kind: 'sending' }
    | { kind: 'done', status: TicketListStatus }
    | { kind: 'failed', message: string }

const PARTY_SIZES = Array.from({ length: MAX_PARTY_SIZE }, (_, index) => index + 1)

export const TicketListForm: FC<TicketListFormProps> = ({ pass, onPassChange }): ReactNode => {
    const [state, setState] = useState<FormState>({ kind: 'idle' })
    const [errors, setErrors] = useState<FieldErrors<TicketListField>>({})

    const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const form = event.currentTarget
        const values = Object.fromEntries(new FormData(form).entries()) as Record<string, unknown>
        const localErrors = validateTicketList(readTicketList(values))

        setErrors(localErrors)

        if (Object.keys(localErrors).length) {
            const firstField = Object.keys(localErrors)[0]
            form.querySelector<HTMLElement>(`[name="${firstField}"]`)?.focus()
            return
        }

        setState({ kind: 'sending' })

        try {
            const response = await fetch('/api/ticket-list', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const result = (await response.json().catch(() => null)) as TicketListResponse | null

            if (result?.ok) {
                setState({ kind: 'done', status: result.status })
                return
            }

            setErrors(result?.fieldErrors ?? {})
            setState({ kind: 'failed', message: result?.message ?? 'Something went wrong. Please try again.' })
        }
        catch {
            setState({ kind: 'failed', message: 'We could not reach the ticket desk. Check your connection and try again.' })
        }
    }

    if (state.kind === 'done') {
        return (
            <div role='status' className='flex min-h-[22rem] flex-col items-start justify-center gap-4'>
                <CircleCheck aria-hidden='true' className='size-10 text-lime' />

                <h3 className='font-heading text-4xl font-extrabold uppercase leading-[0.92] md:text-5xl'>
                    {state.status === 'updated' ? 'Your spot is updated.' : "You're on the list."}
                </h3>

                <p className='max-w-sm text-base leading-relaxed text-cream/80'>
                    {`We'll email you before passes go on sale on ${EVENT.ticketsOnSale}, with 48 hours to buy before everyone else.`}
                </p>

                <button
                    type='button'
                    onClick={() => setState({ kind: 'idle' })}
                    className={cn(buttonBase, buttonVariants['outline-cream'], 'mt-2')}
                >
                    Add someone else
                </button>
            </div>
        )
    }

    const sending = state.kind === 'sending'

    return (
        <form noValidate onSubmit={submit} className='relative flex flex-col gap-5'>
            <HoneypotField />

            <div className='grid gap-5 sm:grid-cols-2'>
                <Field id='ticket-name' label='Name' optional>
                    <input
                        id='ticket-name'
                        name='name'
                        type='text'
                        autoComplete='name'
                        className={inputClasses}
                    />
                </Field>

                <Field id='ticket-email' label='Email' error={errors.email}>
                    <input
                        id='ticket-email'
                        name='email'
                        type='email'
                        inputMode='email'
                        autoComplete='email'
                        required
                        placeholder='you@example.com'
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? 'ticket-email-error' : undefined}
                        className={inputClasses}
                    />
                </Field>
            </div>

            <div className='grid gap-5 sm:grid-cols-[1fr_9rem]'>
                <Field id='ticket-pass' label='Pass' error={errors.pass}>
                    <SelectShell>
                        <select
                            id='ticket-pass'
                            name='pass'
                            value={pass}
                            onChange={(event) => onPassChange(event.target.value)}
                            aria-invalid={Boolean(errors.pass)}
                            className={selectClasses}
                        >
                            {PASSES.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {`${item.name}, $${item.price} ${item.unit}`}
                                </option>
                            ))}
                        </select>
                    </SelectShell>
                </Field>

                <Field id='ticket-party' label='People' error={errors.partySize}>
                    <SelectShell>
                        <select
                            id='ticket-party'
                            name='partySize'
                            defaultValue='2'
                            aria-invalid={Boolean(errors.partySize)}
                            className={selectClasses}
                        >
                            {PARTY_SIZES.map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                    </SelectShell>
                </Field>
            </div>

            <button
                type='submit'
                disabled={sending}
                className={cn(buttonBase, buttonVariants.lime, 'mt-1 w-full')}
            >
                {sending && <LoaderCircle aria-hidden='true' className='size-4 animate-spin motion-reduce:animate-none' />}
                {sending ? 'Adding you' : 'Join the ticket list'}
            </button>

            <p aria-live='polite' className={cn('text-sm', state.kind === 'failed' ? 'text-coral' : 'sr-only')}>
                {state.kind === 'failed' ? state.message : ''}
            </p>

            <p className='text-xs text-cream/50'>
                One email before the sale starts, no newsletter.
            </p>
        </form>
    )
}
